import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";
import { brandNameToSlug } from "../../view_models/products";

import { ProductTileSaveIcon } from "./ProductTileSaveIcon";

/** Convert a GIF URL to a static (first-frame) data URL so it displays as a still image */
function useStaticImageUrl(imageUrl: string): string {
  const [staticUrl, setStaticUrl] = useState<string>(imageUrl);
  const isGif = /\.gif$/i.test(imageUrl);

  useEffect(() => {
    if (!isGif) {
      setStaticUrl(imageUrl);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          setStaticUrl(canvas.toDataURL("image/png"));
        } else {
          setStaticUrl(imageUrl);
        }
      } catch {
        setStaticUrl(imageUrl);
      }
    };
    img.onerror = () => {
      if (!cancelled) setStaticUrl(imageUrl);
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
      img.src = "";
    };
  }, [imageUrl, isGif]);

  return staticUrl;
}

interface DiscoveryProductTileProps {
  imageUrl: string;
  brandName: string;
  brandAvatarUrl?: string;
  rating?: number;
  minOrder?: string;
  freeShipping?: boolean;
  topShop?: boolean;
  onFavorite?: () => void;
  isFavorited?: boolean;
  onClick?: () => void;
  className?: string;
  productName?: string;
  price?: number;
  msrp?: number;
  /** When true, hide brand name link (e.g. for product-only recommendation tiles) */
  hideBrand?: boolean;
  /** When true, hide the rating + min order line (e.g. PDP Refresh brand mini-shop variant) */
  hideRatingAndMinOrder?: boolean;
  /** When true, hide the free shipping line (e.g. PDP Refresh brand mini-shop variant) */
  hideFreeShipping?: boolean;
  /** When true, use a smaller font size for the price line to prevent wrapping in narrow containers */
  compactPrice?: boolean;
  /** When true, tighten the vertical spacing under the price row (4px → 1px). Used for denser SRP grids. */
  tightSpacing?: boolean;
  /** When true, truncate all text lines to prevent wrapping */
  noWrap?: boolean;
  /** When true, show the wholesale price and a one-line product name together, tighten brand spacing, and hide MSRP, minimum, and rating. */
  condensedMetadata?: boolean;
  /** When true, use narrower/mobile-friendly layouts (e.g. stacked brand+city, repositioned image badges). */
  compactLayout?: boolean;
  /** When true, show a quick-add button in the bottom-right of the image */
  showQuickAdd?: boolean;
  /** Callback fired when the quick-add button is clicked */
  onQuickAdd?: () => void;
  /** When > 0, show active state: dark pill with minus, quantity, plus */
  quickAddQuantity?: number;
  /** Callback to increment quantity (active state) */
  onQuickAddIncrement?: () => void;
  /** Callback to decrement quantity (active state) */
  onQuickAddDecrement?: () => void;
  /** When true, hide the heart/favorite button (e.g. when replaced by a bookmark overlay) */
  hideHeart?: boolean;
  /** Optional badge label shown in the top-left of the image (e.g. "Bestseller"). When provided, overrides the topShop badge. */
  badge?: string;
  /** When true, show a "find similar" (magnifying glass) button in the bottom-left of the image, mirroring the quick-add button. */
  showFindSimilar?: boolean;
  /** Callback fired when the "find similar" button is clicked. */
  onFindSimilar?: () => void;
  /** When true (default), the "find similar" button grows into a pill revealing its label while the tile is hovered, matching the redesigned tile. Set to false for touch/mobile tiles, which have no hover gesture. */
  findSimilarExpandsOnHover?: boolean;
  /** When true, drops every pointer-hover affordance (find similar expansion, quick-add invert, heart and brand-link hover states). Set on touch/mobile previews, where a hover state can never be reached on the real device. */
  disableHoverAffordances?: boolean;
  /** Sale text (e.g. "Up to 20% off"), shown in red below the free shipping row. */
  saleLabel?: string;
  /** When true, removes the image's rounded corners (e.g. for full-bleed native-app mobile grids, which use square tiles). Defaults to the standard rounded corners. */
  squareCorners?: boolean;
  /** Optional brand city shown for local hub rationale tiles. */
  brandCity?: string;
  /** How to display `brandCity` when provided. */
  brandCityVariant?: BrandCityVariant;
}

export type BrandCityVariant =
  | "below-brand"
  | "inline-brand"
  | "image-badge"
  | "inline-meta";

export default function DiscoveryProductTile({
  imageUrl,
  brandName,
  brandAvatarUrl,
  rating,
  minOrder,
  freeShipping = false,
  topShop = false,
  onFavorite,
  isFavorited = false,
  onClick,
  className = "",
  productName,
  price,
  msrp,
  hideBrand = false,
  hideRatingAndMinOrder = false,
  hideFreeShipping = false,
  compactPrice = false,
  tightSpacing = false,
  noWrap = false,
  condensedMetadata = false,
  compactLayout = false,
  showQuickAdd = false,
  onQuickAdd,
  quickAddQuantity = 0,
  onQuickAddIncrement,
  onQuickAddDecrement,
  hideHeart = false,
  badge,
  showFindSimilar = false,
  onFindSimilar,
  findSimilarExpandsOnHover = true,
  disableHoverAffordances = false,
  saleLabel,
  squareCorners = false,
  brandCity,
  brandCityVariant = "below-brand",
}: DiscoveryProductTileProps) {
  const isQuickAddActive = quickAddQuantity > 0;
  const displayImageUrl = useStaticImageUrl(imageUrl);
  const { src: resolvedImageSrc, onError: onProductImageError } =
    usePlaygroundImageWithFallback(displayImageUrl);
  const findSimilarClipId = `clip0_find_similar_tile_${React.useId()}`;
  const [heartHovered, setHeartHovered] = useState(false);

  // Star icon component
  const StarIcon = () => (
    <svg
      className="h-3 w-3"
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-star"
      role="img"
      style={{ color: "#333333", fill: "#333333", fontSize: "12px" }}
    >
      <path
        d="m12.844 1.87 2.713 5.501a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .515 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.416-2.848a.94.94 0 0 0-.874 0l-5.43 2.856a.94.94 0 0 1-1.37-.99L5.8 15.5a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.079-.89a.94.94 0 0 0 .708-.514l2.714-5.5a.941.941 0 0 1 1.687 0Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-star">Star</title>
    </svg>
  );

  // Find similar icon (magnifying glass + sparkle) — bottom-left, mirrors quick-add
  const FindSimilarIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <g clipPath={`url(#${findSimilarClipId})`}>
        <rect width="16" height="16" fill="white" />
        <path
          d="M9.89584 11.5091C8.87995 12.525 7.50212 13.0957 6.06544 13.0957C4.62876 13.0957 3.25092 12.525 2.23504 11.5091C1.21916 10.4932 0.648438 9.1154 0.648438 7.67872C0.648437 6.24204 1.21916 4.86421 2.23504 3.84832C3.25092 2.83244 4.62876 2.26172 6.06544 2.26172C7.50212 2.26172 8.87995 2.83244 9.89584 3.84832C10.9117 4.86421 11.4824 6.24204 11.4824 7.67872C11.4824 9.1154 10.9117 10.4932 9.89584 11.5091ZM9.89584 11.5091L13.6485 15.2617"
          stroke="#333333"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.1309 -0.525391C11.4483 -0.525388 11.846 -0.346138 11.9854 0.0761719L12.0098 0.163086L12.1299 0.658203C12.4134 1.7464 12.7248 2.38687 13.1689 2.83105C13.6765 3.33862 14.4403 3.67276 15.8359 3.99023C16.321 4.10056 16.5254 4.53062 16.5254 4.86914C16.5254 5.20756 16.3216 5.63755 15.8369 5.74805C14.4411 6.06554 13.6766 6.3996 13.1689 6.90723C12.6614 7.41479 12.3272 8.17861 12.0098 9.57422C11.8994 10.0593 11.4694 10.2637 11.1309 10.2637C10.7923 10.2637 10.3623 10.0593 10.252 9.57422C9.93448 8.17861 9.60034 7.41479 9.09277 6.90723C8.58511 6.3996 7.82065 6.06554 6.4248 5.74805C5.94006 5.63758 5.73633 5.20758 5.73633 4.86914C5.73633 4.5307 5.94005 4.10071 6.4248 3.99023C7.82063 3.67271 8.5851 3.33873 9.09277 2.83105C9.60045 2.32338 9.93443 1.55891 10.252 0.163086C10.3624 -0.321668 10.7924 -0.525393 11.1309 -0.525391Z"
          fill="#333333"
          stroke="white"
          strokeWidth="1.25"
        />
      </g>
      <defs>
        <clipPath id={findSimilarClipId}>
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const FreeShippingIcon = () => (
    <svg
      className="h-3 w-3"
      focusable="false"
      viewBox="0 0 16 16"
      aria-labelledby="titleAccess-insider"
      role="img"
      style={{
        color: "#36676a",
        fill: "#36676a",
        width: "12px",
        height: "12px",
      }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.874 8A4.002 4.002 0 0 1 1 7a4 4 0 0 1 7.874-1H15v3.75h-3V8zM5 9.222a2.222 2.222 0 1 0 0-4.444 2.222 2.222 0 0 0 0 4.444"
        fill="currentColor"
      />
      <title id="titleAccess-insider">Insider</title>
    </svg>
  );

  const LocationPinIcon = ({ className }: { className?: string }) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
    >
      <path
        d="M8 1.5C5.51472 1.5 3.5 3.51472 3.5 6C3.5 9.25 8 14.5 8 14.5C8 14.5 12.5 9.25 12.5 6C12.5 3.51472 10.4853 1.5 8 1.5Z"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <circle cx="8" cy="6" r="1.75" fill="currentColor" />
    </svg>
  );

  const showBrandCityOnImage =
    brandCity != null && brandCityVariant === "image-badge";
  const showBrandCityBelowBrand =
    brandCity != null && brandCityVariant === "below-brand";
  const showBrandCityInline =
    brandCity != null && brandCityVariant === "inline-brand";
  const showBrandCityInlineMeta =
    brandCity != null && brandCityVariant === "inline-meta";
  const useStackedInlineBrand = showBrandCityInline && compactLayout;
  const brandLinkHoverClass = disableHoverAffordances
    ? ""
    : "hover:text-[#757575]";
  const imageActionZIndexClass = showBrandCityOnImage ? "z-20" : "z-5";

  return (
    <div
      className={cn(
        "group relative w-full",
        "[&_button]:cursor-pointer [&_button:disabled]:cursor-default",
        className
      )}
      onClick={onClick}
      // Tiles always read as clickable, even in prototypes that haven't wired
      // up a destination yet.
      style={{ cursor: "pointer" }}
    >
      {/* Image Container (Square, with rounded corners unless `squareCorners`) */}
      <div
        className="relative w-full overflow-hidden"
        style={{
          aspectRatio: "1 / 1",
          borderRadius: squareCorners ? 0 : "4px",
        }}
      >
        {/* Background Image — GIFs are converted to static first frame */}
        <img
          src={resolvedImageSrc}
          alt={brandName}
          className="absolute inset-0 h-full w-full object-cover"
          referrerPolicy="no-referrer"
          onError={onProductImageError}
        />

        {/* Transparent overlay - black with 5% opacity (scrim for white/transparent backgrounds) */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          }}
        />

        {/* Top-left Badge — "Bestseller", "Top Shop", or custom label */}
        {(badge ?? (topShop ? "Top Shop" : null)) && (
          <div className="absolute top-2 left-2 z-10">
            <div className="bg-fs-surface-primary rounded-sm px-2 py-1">
              <p className="text-fs-text-primary text-xs font-medium">
                {badge ?? "Top Shop"}
              </p>
            </div>
          </div>
        )}

        {showBrandCityOnImage ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-(--slate-color-grey-1000)/55 via-(--slate-color-grey-1000)/25 to-transparent px-2 pt-6 pb-1.5">
            <div className="type-fs-label text-fs-text-primary-inverse flex min-w-0 items-center gap-1">
              <LocationPinIcon className="shrink-0" />
              <span className="truncate">{brandCity}</span>
            </div>
          </div>
        ) : null}

        {/* Heart Icon - Top Right */}
        {!hideHeart && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onFavorite?.();
            }}
            onMouseEnter={
              disableHoverAffordances ? undefined : () => setHeartHovered(true)
            }
            onMouseLeave={
              disableHoverAffordances ? undefined : () => setHeartHovered(false)
            }
            className="absolute top-2 right-2 z-10 flex size-6 items-center justify-center"
            aria-label={isFavorited ? "Un-follow" : "Follow"}
            style={{ display: onFavorite ? "flex" : "none" }}
          >
            <ProductTileSaveIcon filled={isFavorited} hovered={heartHovered} />
          </button>
        )}

        {/* Quick Add - Bottom Right: plus circle (default) or dark pill with minus, quantity, plus (active, ref design) */}
        {showQuickAdd &&
          (isQuickAddActive ? (
            <div
              className={cn(
                "absolute flex items-center justify-center gap-0 transition-all duration-200",
                imageActionZIndexClass
              )}
              style={{
                bottom: "7.5px",
                right: "7.5px",
                height: "32px",
                paddingLeft: "10px",
                paddingRight: "10px",
                borderRadius: "100px",
                backgroundColor: "#333333",
                fontFamily: "var(--slate-typography-family-sans)",
                fontSize: "14px",
                fontWeight: 500,
                color: "white",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onQuickAddDecrement?.();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  padding: 0,
                  border: "none",
                  background: "none",
                  color: "white",
                  cursor: "pointer",
                }}
                aria-label="Decrease quantity"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                </svg>
              </button>
              <span style={{ minWidth: 20, textAlign: "center" }}>
                {quickAddQuantity}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onQuickAddIncrement?.();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  padding: 0,
                  border: "none",
                  background: "none",
                  color: "white",
                  cursor: "pointer",
                }}
                aria-label="Increase quantity"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onQuickAdd?.();
              }}
              className={cn(
                "absolute flex items-center justify-center transition-colors duration-150",
                imageActionZIndexClass
              )}
              style={{
                bottom: "7.5px",
                right: "7.5px",
                width: "32px",
                height: "32px",
                padding: "8px",
                borderRadius: "100px",
                backgroundColor: "white",
                cursor: "pointer",
              }}
              onMouseEnter={
                disableHoverAffordances
                  ? undefined
                  : (e) => {
                      e.currentTarget.style.backgroundColor = "#333";
                      const svg = e.currentTarget.querySelector("svg");
                      if (svg) svg.setAttribute("stroke", "white");
                    }
              }
              onMouseLeave={
                disableHoverAffordances
                  ? undefined
                  : (e) => {
                      e.currentTarget.style.backgroundColor = "white";
                      const svg = e.currentTarget.querySelector("svg");
                      if (svg) svg.setAttribute("stroke", "#333");
                    }
              }
              aria-label="Quick add"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#333"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 1.714v20.572M1.714 12h20.572" />
              </svg>
            </button>
          ))}

        {/* Find Similar - Bottom Left */}
        {showFindSimilar &&
          (findSimilarExpandsOnHover && !disableHoverAffordances ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFindSimilar?.();
              }}
              className={cn(
                "bg-fs-surface-primary absolute bottom-[7.5px] left-[7.5px] flex h-[32px] w-[32px] items-center justify-start overflow-hidden rounded-full transition-[width] duration-200 ease-out",
                "group-hover:w-[124px]",
                imageActionZIndexClass
              )}
              aria-label="Find similar"
            >
              <span className="flex items-center gap-[6px] px-[8px] whitespace-nowrap">
                <FindSimilarIcon />
                {/* Opacity (not just the button's width/overflow-hidden clip) hides this text at rest — the collapsed 32px width only leaves ~2px of clipping margin past the icon, so without this the label's first letter peeks out past the circle. */}
                <span
                  className="text-[14px] leading-[20px] tracking-[0.15px] text-[#333] opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100"
                  style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
                >
                  Find similar
                </span>
              </span>
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFindSimilar?.();
              }}
              className={cn(
                "absolute flex items-center justify-center transition-colors duration-150",
                imageActionZIndexClass
              )}
              style={{
                bottom: "7.5px",
                left: "7.5px",
                width: "32px",
                height: "32px",
                padding: "8px",
                borderRadius: "100px",
                backgroundColor: "white",
                cursor: "pointer",
              }}
              aria-label="Find similar"
            >
              <FindSimilarIcon />
            </button>
          ))}
      </div>

      {/* Product Info — full-bleed native-app mobile grids (`squareCorners`)
          get 8px of horizontal padding so metadata text doesn't run up
          against the screen edge; desktop keeps its existing 4px. */}
      <div
        className={cn(
          "mt-3 flex flex-col items-start",
          squareCorners ? "px-2" : "px-1"
        )}
      >
        {condensedMetadata && price !== undefined && productName ? (
          <div className="mb-px flex w-full min-w-0 items-baseline gap-1">
            <span
              className="text-fs-text-primary shrink-0"
              style={{
                fontFamily: "var(--slate-typography-family-sans)",
                fontSize: "var(--slate-typography-font-size-200)",
                fontWeight: "var(--slate-typography-weight-medium)",
                lineHeight: "var(--slate-typography-line-height-200)",
                letterSpacing: "var(--slate-typography-letter-spacing-200)",
              }}
            >
              ${price.toFixed(2)}
            </span>
            <span className="type-fs-paragraph text-fs-text-primary min-w-0 truncate">
              {productName}
            </span>
          </div>
        ) : null}

        {/* Price with MSRP */}
        {!condensedMetadata && price !== undefined && (
          <div
            className={cn(
              "flex items-baseline gap-2",
              tightSpacing ? "mb-px" : "mb-1"
            )}
            style={
              noWrap
                ? {
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }
                : undefined
            }
          >
            <span
              className="text-fs-text-primary"
              style={{
                fontFamily: "var(--slate-typography-family-sans)",
                fontSize: compactPrice
                  ? "var(--slate-typography-font-size-200)"
                  : "var(--slate-typography-font-size-300)",
                fontWeight: "var(--slate-typography-weight-medium)",
                lineHeight: compactPrice
                  ? "var(--slate-typography-line-height-200)"
                  : "var(--slate-typography-line-height-300)",
                letterSpacing: "var(--slate-typography-letter-spacing-200)",
                flexShrink: 0,
              }}
            >
              ${price.toFixed(2)}
            </span>
            {msrp && msrp > price && (
              <span
                className="type-fs-label text-[#757575]"
                style={
                  noWrap
                    ? { overflow: "hidden", textOverflow: "ellipsis" }
                    : undefined
                }
              >
                MSRP ${msrp.toFixed(2)}
              </span>
            )}
          </div>
        )}

        {/* Product Name */}
        {!condensedMetadata && productName && (
          <p
            className="text-fs-text-primary mb-1 w-full text-sm font-medium"
            style={{
              lineHeight: "1.4",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {productName}
          </p>
        )}

        {/* Brand Name - as link (hidden when hideBrand for recommendation tiles) */}
        {!hideBrand &&
          (showBrandCityInline ? (
            useStackedInlineBrand ? (
              <div
                className={cn(
                  "mb-1 flex min-w-0 flex-col items-start gap-0.5",
                  condensedMetadata || tightSpacing ? "mb-px" : "mb-1"
                )}
              >
                <Link
                  to={`/brand/${brandNameToSlug(brandName)}`}
                  className={cn(
                    "type-fs-paragraph text-fs-text-primary block w-full min-w-0 truncate underline transition-colors",
                    brandLinkHoverClass
                  )}
                >
                  {brandName}
                </Link>
                <span className="type-fs-label text-fs-text-subdued flex min-w-0 items-center gap-1">
                  <LocationPinIcon className="shrink-0" />
                  <span className="truncate">{brandCity}</span>
                </span>
              </div>
            ) : (
              <div
                className={cn(
                  "type-fs-paragraph text-fs-text-primary mb-1 flex min-w-0 items-baseline gap-1",
                  condensedMetadata || tightSpacing ? "mb-px" : "mb-1"
                )}
                style={
                  noWrap
                    ? {
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                      }
                    : undefined
                }
              >
                <Link
                  to={`/brand/${brandNameToSlug(brandName)}`}
                  className={cn(
                    "text-fs-text-primary underline transition-colors",
                    brandLinkHoverClass
                  )}
                >
                  {brandName}
                </Link>
                <span
                  aria-hidden="true"
                  className="text-fs-text-subdued shrink-0"
                >
                  ·
                </span>
                <span className="text-fs-text-subdued shrink-0">
                  {brandCity}
                </span>
              </div>
            )
          ) : (
            <Link
              to={`/brand/${brandNameToSlug(brandName)}`}
              className={cn(
                "type-fs-paragraph text-fs-text-primary underline transition-colors",
                brandLinkHoverClass,
                condensedMetadata || tightSpacing ? "mb-px" : "mb-1"
              )}
              style={
                noWrap
                  ? {
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      display: "block",
                      maxWidth: "100%",
                    }
                  : undefined
              }
            >
              {brandName}
            </Link>
          ))}

        {showBrandCityBelowBrand ? (
          <p
            className={cn(
              "type-fs-label text-fs-text-subdued",
              tightSpacing ? "mb-px" : "mb-1"
            )}
          >
            {brandCity}
          </p>
        ) : null}

        {/* Min Order and Rating — min first, then a "·" separator and the
            star rating, but only when there's a genuine (non-zero) rating to
            show. A `0` rating means "no reviews yet", so we omit the star
            block entirely rather than display "★ 0.0". */}
        {!condensedMetadata && !hideRatingAndMinOrder && (
          <div
            className="text-fs-text-primary flex items-center gap-1 text-sm"
            style={
              noWrap
                ? {
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }
                : undefined
            }
          >
            <span>
              {minOrder && minOrder !== "undefined" ? minOrder : "$250 min"}
            </span>
            {rating != null && rating > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <div
                  className="flex items-center gap-1"
                  style={{ flexShrink: 0 }}
                >
                  <StarIcon />
                  <span>{rating.toFixed(1)}</span>
                </div>
              </>
            )}
            {showBrandCityInlineMeta ? (
              <>
                <span aria-hidden="true">·</span>
                <span className="text-fs-text-subdued shrink-0">
                  {brandCity}
                </span>
              </>
            ) : null}
          </div>
        )}

        {/* Free Shipping - separate row */}
        {!hideFreeShipping && freeShipping && (
          <div
            className={cn(
              "flex items-center gap-1",
              tightSpacing ? "mt-px" : "mt-1"
            )}
            style={
              noWrap ? { overflow: "hidden", whiteSpace: "nowrap" } : undefined
            }
          >
            <FreeShippingIcon />
            <span className="text-sm text-[#154548]">Free shipping</span>
          </div>
        )}

        {/* Sale label - red text, e.g. "Up to 20% off" */}
        {saleLabel && (
          <p
            className={cn(
              "text-sm",
              tightSpacing ? "mt-px" : "mt-1",
              noWrap ? "overflow-hidden whitespace-nowrap" : ""
            )}
            style={{ color: "var(--slate-color-message-text-sale)" }}
          >
            {saleLabel}
          </p>
        )}
      </div>
    </div>
  );
}
